import { useRef, useState, useEffect, type ReactNode } from "react";

interface LazyOnScrollProps {
  children: ReactNode;
  rootMargin?: string;
  fallback?: ReactNode;
}

/**
 * Only renders children when the placeholder scrolls into view.
 * This prevents React.lazy chunks from loading eagerly on page load.
 */
const LazyOnScroll = ({ children, rootMargin = "200px", fallback = null }: LazyOnScrollProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [rootMargin]);

  if (visible) return <>{children}</>;
  return <div ref={ref} style={{ minHeight: 100 }}>{fallback}</div>;
};

export default LazyOnScroll;
